/**
 * ErrorBoundary Component
 * React error boundary to catch and handle component errors gracefully
 *
 * Features:
 * - Catches runtime errors in React components
 * - Shows fallback UI with error details
 * - Supports custom fallback components
 * - Logs errors for debugging
 * - Allows error recovery with reset
 */

import { Component, ReactNode } from 'react';
import { Button, Typography, theme, Card } from 'antd';
import {
  CloseCircleOutlined,
  HomeOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { controlHeightTokens } from '@/styles/tokens';
import { useTranslation } from 'react-i18next';

const { Title, Text, Paragraph } = Typography;

/**
 * Error Boundary State
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: { componentStack: string } | null;
}

/**
 * Error Boundary Props
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, errorInfo: { componentStack: string }, reset: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: { componentStack: string }) => void;
  level?: 'page' | 'component'; // Controls fallback UI style
}

/**
 * Error Boundary Class Component
 */
class ErrorBoundaryClass extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      error: null,
      errorInfo: null,
      hasError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error, hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack: string }) {
    // Log error
    console.error('[Error Boundary]', error, errorInfo);

    // Update state
    this.setState({
      error,
      errorInfo,
    });

    // Call onError callback if provided
    this.props.onError?.(error, errorInfo);

    // TODO: Send error to error tracking service (e.g., Sentry)
  }

  resetError = () => {
    this.setState({
      error: null,
      errorInfo: null,
      hasError: false,
    });
  };

  render() {
    if (this.state.hasError && this.state.error && this.state.errorInfo) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorInfo, this.resetError);
      }

      // Use default fallback based on level
      return this.props.level === 'component' ? (
        <ComponentErrorFallback error={this.state.error} reset={this.resetError} />
      ) : (
        <PageErrorFallback error={this.state.error} reset={this.resetError} />
      );
    }

    return this.props.children;
  }
}

/**
 * Page-level Error Fallback
 */
function PageErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  const { token } = theme.useToken();
  const { t } = useTranslation(['common']);

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: `${token.paddingXL}px`,
        backgroundColor: token.colorBgLayout,
        textAlign: 'center',
      }}
    >
      {/* Error Icon */}
      <div
        style={{
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          backgroundColor: token.colorErrorBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: token.marginLG,
        }}
      >
        <CloseCircleOutlined
          style={{
            fontSize: token.sizeXXL * 1.5,
            color: token.colorError,
          }}
        />
      </div>

      {/* Error Title */}
      <Title level={2} style={{ marginBottom: token.marginSM, color: token.colorText }}>
        {t('common:errors.boundary.title', 'Something Went Wrong')}
      </Title>

      {/* Error Message */}
      <Paragraph
        style={{
          fontSize: token.fontSize,
          color: token.colorTextSecondary,
          marginBottom: token.marginLG,
          maxWidth: '500px',
        }}
      >
        {t(
          'common:errors.boundary.description',
          "We're sorry, but something unexpected happened. Please try refreshing the page or return home."
        )}
      </Paragraph>

      {/* Action Buttons */}
      <div className="tw-flex tw-gap-3">
        <Button
          icon={<HomeOutlined />}
          size="large"
          onClick={handleGoHome}
          style={{
            height: `${controlHeightTokens.controlHeightXL}px`,
            borderRadius: `${token.borderRadius}px`,
          }}
        >
          {t('common:errors.boundary.goHome', 'Go to Home')}
        </Button>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          size="large"
          onClick={reset}
          style={{
            height: `${controlHeightTokens.controlHeightXL}px`,
            borderRadius: `${token.borderRadius}px`,
          }}
        >
          {t('common:errors.boundary.tryAgain', 'Try Again')}
        </Button>
      </div>

      {/* Technical Details (Development Only) */}
      {import.meta.env.DEV && (
        <details
          style={{
            marginTop: token.marginLG,
            textAlign: 'left',
            maxWidth: '600px',
            width: '100%',
          }}
        >
          <summary
            style={{
              cursor: 'pointer',
              color: token.colorTextSecondary,
              fontSize: token.fontSizeSM,
              marginBottom: token.marginSM,
            }}
          >
            {t('common:errors.boundary.technicalDetails', 'Technical Details')}
          </summary>
          <pre
            style={{
              padding: token.paddingSM,
              backgroundColor: token.colorBgContainer,
              borderRadius: `${token.borderRadius}px`,
              fontSize: token.fontSizeSM,
              overflow: 'auto',
              maxHeight: '200px',
              border: `1px solid ${token.colorBorder}`,
            }}
          >
            {error.message}
            {'\n\n'}
            {error.stack}
          </pre>
        </details>
      )}
    </div>
  );
}

/**
 * Component-level Error Fallback
 */
function ComponentErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  const { token } = theme.useToken();
  const { t } = useTranslation(['common']);

  return (
    <Card
      style={{
        margin: token.marginMD,
        borderRadius: `${token.borderRadiusLG}px`,
        border: `2px solid ${token.colorErrorBorder}`,
        backgroundColor: token.colorErrorBg,
      }}
      styles={{
        body: {
          padding: token.paddingLG,
        },
      }}
    >
      <div className="tw-flex tw-flex-col tw-items-center tw-text-center">
        {/* Error Icon */}
        <WarningOutlined
          style={{
            fontSize: token.sizeXXL,
            color: token.colorError,
            marginBottom: token.marginMD,
          }}
        />

        {/* Error Message */}
        <Text strong style={{ color: token.colorText, marginBottom: token.marginXS }}>
          {t('common:errors.boundary.componentError', 'Component Failed to Load')}
        </Text>
        <Text
          type="secondary"
          style={{
            fontSize: token.fontSizeSM,
            marginBottom: token.marginMD,
          }}
        >
          {t(
            'common:errors.boundary.componentErrorDescription',
            'This component encountered an error. The rest of the page should still work.'
          )}
        </Text>

        {/* Error Details (Development) */}
        {import.meta.env.DEV && (
          <Text
            type="secondary"
            style={{
              fontSize: token.fontSizeSM,
              marginBottom: token.marginMD,
              fontFamily: 'monospace',
            }}
          >
            {error.message}
          </Text>
        )}

        {/* Retry Button */}
        <Button
          type="primary"
          danger
          icon={<ReloadOutlined />}
          onClick={reset}
          style={{
            height: `${controlHeightTokens.controlHeight}px`,
            borderRadius: `${token.borderRadius}px`,
          }}
        >
          {t('common:errors.boundary.retry', 'Retry')}
        </Button>
      </div>
    </Card>
  );
}

/**
 * Export ErrorBoundary component
 */
export const ErrorBoundary = ErrorBoundaryClass;

/**
 * HOC to wrap component with error boundary
 */
// eslint-disable-next-line react-refresh/only-export-components
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}

export default ErrorBoundary;
